import React, { useMemo, useState } from 'react';
import { IconButton, Popover, Box, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, Divider, Chip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { motion } from 'framer-motion';

import {
  IconButtonGroup,
  CustomBadge,
  IconStyle,
} from '../../styles/OwnerStyle/BusinessInfoStyles';

import { useTranslation } from 'react-i18next';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso ?? '';
  }
};

const StaffNotification = ({
  notifications = [],
  onMarkSeen,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const sorted = useMemo(
    () =>
      [...notifications].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [notifications]
  );

  const unseenIds = useMemo(
    () => sorted.filter(n => !n.isSeen).map(n => n.notificationId),
    [sorted]
  );

  const unseenCount = unseenIds.length;

  const handleOpen = (e) => setAnchorEl(e.currentTarget);

  const handleClose = async () => {
    if (unseenCount > 0 && typeof onMarkSeen === 'function') {
      try {
        await onMarkSeen(unseenIds);
      } catch (err) {
      }
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const { t } = useTranslation('StaffNotification');
  return (
    <>
      <IconButtonGroup>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <CustomBadge
            badgeContent={unseenCount}
            sx={{ "& .MuiBadge-badge": { backgroundColor: 'orange', color: 'black' } }}
          >
            <IconButton onClick={handleOpen}>
              <NotificationsIcon sx={IconStyle} />
            </IconButton>
          </CustomBadge>
        </motion.div>
      </IconButtonGroup>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
      >
        <Box sx={{ minWidth: 340, maxWidth: 420, maxHeight: 420, overflowY: 'auto', p: 1 }}>
          {sorted.length === 0 ? (
            <Box p={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('none')}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {sorted.map((n, idx) => (
                <React.Fragment key={n.notificationId ?? idx}>
                  <ListItem alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'warning.main' }}>
                        <NotificationsIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body1">{n.message}</Typography>
                          {!n.isSeen && (
                            <Chip
                              label={t('new')}
                              size="small"
                              sx={{ height: 20 }}
                              color="warning"
                              variant="filled"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(n.createdAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < sorted.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default StaffNotification;
